import { WebElement, ISize, IRectangle, Locator, WebElementPromise } from 'selenium-webdriver';
import { DrawerPopupWebElementBase } from './DrawerPopupWebElementBase';

/**
 * The component WebElement for [oj-c-drawer-popup](../../jsdocs/oj-c.DrawerPopup.html).
 * Do not instantiate this class directly, instead, use
 * [findDrawerPopup](../functions/findDrawerPopup.html).
 */
export class DrawerPopupWebElement extends DrawerPopupWebElementBase {
  public async isDisplayed(): Promise<boolean> {
    const content = await this.getContent();
    if (!content) return false;
    return content.isDisplayed();
  }

  public getSize(): Promise<ISize> {
    return this.getContent()
      .then((content) => content.getRect())
      .then((rect) => Promise.resolve({ width: rect.width, height: rect.height }));
  }

  public getRect(): Promise<IRectangle> {
    return this.getContent().then((content) => content.getRect());
  }

  public getText(): Promise<string> {
    return this.getContent().then((content) => content.getText());
  }

  public findElement(locator: Locator): WebElementPromise {
    const p = this.whenReady()
      .then(() => this.getContent())
      .then((content) => content.findElement(locator));
    return new WebElementPromise(this.getDriver(), p);
  }

  public async findElements(locator: Locator): Promise<WebElement[]> {
    const content = await this.getContent();
    return content.findElements(locator);
  }

  private async getContent(): Promise<WebElement> {
    await this.whenBusyContextReady();
    const content = await this.getDriver().executeScript<WebElement>((el: any) => {
      const LAYER_CONTENT = Symbol.for('__oj_c_layer_content');
      return el[LAYER_CONTENT];
    }, this);
    return content;
  }

  /**
   * Dismisses the drawer.
   */
  public doClose(): Promise<void> {
    return this.setProperty('opened', false);
  }

  /**
   * Sets the value of <code>opened</code> property.
   * Specifies whether the Drawer is open.
   * @param opened The value to set for <code>opened</code>
   * @deprecated Since 18.0.0. Use a launcher action to open the drawer and <code>doClose</code> to dismiss it.
   */
  public changeOpened(opened: boolean): Promise<void> {
    const localWhenReady = this.whenReady;
    this.whenReady = this.whenBusyContextReady;
    try {
      return super.changeOpened(opened);
    } finally {
      this.whenReady = localWhenReady;
    }
  }
}
